// SPDX-License-Identifier: AGPL-3.0-or-later

import type {GuildReadyData} from '@app/features/gateway/types/GatewayGuildTypes';
import type {VoiceState} from '@app/features/gateway/types/GatewayVoiceTypes';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {ME} from '@app/vendor/constants/AppConstants';
import {runInAction} from 'mobx';

const logger = new Logger('VoiceStateGatewayHandler');
export class VoiceStateGatewayHandler {
	constructor(
		private getVoiceStates: () => Record<string, Record<string, Record<string, VoiceState>>>,
		private getUserVoiceStates: () => Record<string, Record<string, Record<string, VoiceState>>>,
		private getConnectionVoiceStates: () => Record<string, VoiceState>,
		private setVoiceStates: (states: Record<string, Record<string, Record<string, VoiceState>>>) => void,
		private setUserVoiceStates: (states: Record<string, Record<string, Record<string, VoiceState>>>) => void,
		private setConnectionVoiceStates: (states: Record<string, VoiceState>) => void,
		private removeVoiceState: (connectionId: string, userId: string, guildId: string) => void,
	) {}
	handleGatewayVoiceStateUpdate(guildId: string | null, voiceState: VoiceState): void {
		const key = guildId ?? ME;
		const channelId = voiceState.channel_id;
		const userId = voiceState.user_id;
		const connectionId = voiceState.connection_id;
		if (!connectionId) {
			logger.warn('Voice state missing connection_id:', voiceState);
			return;
		}
		const vsWithGuild = {...voiceState, guild_id: key};
		if (!channelId) {
			this.removeVoiceState(connectionId, userId, key);
			logger.debug('User left voice channel', {
				guildId: key,
				userId,
				connectionId,
			});
			return;
		}
		runInAction(() => {
			const newVoiceStates = {...this.getVoiceStates()};
			const newUserVoiceStates = {...this.getUserVoiceStates()};
			const newConnectionVoiceStates = {...this.getConnectionVoiceStates()};
			const oldVoiceState = this.getConnectionVoiceStates()[connectionId];
			if (oldVoiceState?.channel_id && oldVoiceState.guild_id) {
				const oldGuildId = oldVoiceState.guild_id;
				const oldGuildStates = {...(newVoiceStates[oldGuildId] || {})};
				const oldChannelStates = {...(oldGuildStates[oldVoiceState.channel_id] || {})};
				delete oldChannelStates[connectionId];
				if (Object.keys(oldChannelStates).length === 0) {
					delete oldGuildStates[oldVoiceState.channel_id];
				} else {
					oldGuildStates[oldVoiceState.channel_id] = oldChannelStates;
				}
				if (Object.keys(oldGuildStates).length === 0) {
					delete newVoiceStates[oldGuildId];
				} else {
					newVoiceStates[oldGuildId] = oldGuildStates;
				}
				const oldUserId = oldVoiceState.user_id;
				const oldUserStates = {...(newUserVoiceStates[oldUserId] || {})};
				const oldUserGuildStates = {...(oldUserStates[oldGuildId] || {})};
				delete oldUserGuildStates[connectionId];
				if (Object.keys(oldUserGuildStates).length === 0) {
					delete oldUserStates[oldGuildId];
				} else {
					oldUserStates[oldGuildId] = oldUserGuildStates;
				}
				if (Object.keys(oldUserStates).length === 0) {
					delete newUserVoiceStates[oldUserId];
				} else {
					newUserVoiceStates[oldUserId] = oldUserStates;
				}
			}
			const guildStates = {...(newVoiceStates[key] || {})};
			const channelStates = {...(guildStates[channelId] || {})};
			channelStates[connectionId] = vsWithGuild;
			guildStates[channelId] = channelStates;
			newVoiceStates[key] = guildStates;
			const userStates = {...(newUserVoiceStates[userId] || {})};
			const guildUserStates = {...(userStates[key] || {})};
			guildUserStates[connectionId] = vsWithGuild;
			userStates[key] = guildUserStates;
			newUserVoiceStates[userId] = userStates;
			newConnectionVoiceStates[connectionId] = vsWithGuild;
			this.setVoiceStates(newVoiceStates);
			this.setUserVoiceStates(newUserVoiceStates);
			this.setConnectionVoiceStates(newConnectionVoiceStates);
		});
		logger.debug('Voice state updated', {
			guildId: key,
			channelId,
			userId,
			connectionId,
			selfMute: voiceState.self_mute,
			selfDeaf: voiceState.self_deaf,
			selfVideo: voiceState.self_video,
			selfStream: voiceState.self_stream,
			viewerStreamKeys: voiceState.viewer_stream_keys,
		});
	}
	handleGatewayVoiceStateDelete(guildId: string, userId: string): void {
		const userStates = this.getUserVoiceStates()[userId];
		if (!userStates) {
			logger.debug('No voice states found for user', {userId, guildId});
			return;
		}
		const guildStates = userStates[guildId];
		if (!guildStates) {
			logger.debug('No voice state found for user in guild', {userId, guildId});
			return;
		}
		for (const voiceState of Object.values(guildStates)) {
			const connectionId = voiceState.connection_id;
			if (!connectionId) {
				logger.warn('Voice state missing connection_id in delete:', voiceState);
				continue;
			}
			this.removeVoiceState(connectionId, userId, guildId);
			logger.debug('Voice state deleted', {guildId, userId, connectionId});
		}
	}
	handleConnectionOpen(guilds: Array<GuildReadyData>): void {
		runInAction(() => {
			const newVoiceStates: Record<string, Record<string, Record<string, VoiceState>>> = {};
			const newUserVoiceStates: Record<string, Record<string, Record<string, VoiceState>>> = {};
			const newConnectionVoiceStates: Record<string, VoiceState> = {};
			for (const guild of guilds) {
				if (!guild.voice_states || guild.voice_states.length === 0) {
					continue;
				}
				for (const voiceState of guild.voice_states) {
					if (!voiceState.channel_id) {
						continue;
					}
					const connectionId = voiceState.connection_id;
					if (!connectionId) {
						logger.warn('Skipping voice state without connection_id', {
							guildId: guild.id,
							userId: voiceState.user_id,
						});
						continue;
					}
					if (!newVoiceStates[guild.id]) {
						newVoiceStates[guild.id] = {};
					}
					if (!newVoiceStates[guild.id][voiceState.channel_id]) {
						newVoiceStates[guild.id][voiceState.channel_id] = {};
					}
					const vsWithGuild = {...voiceState, guild_id: guild.id};
					newVoiceStates[guild.id][voiceState.channel_id][connectionId] = vsWithGuild;
					if (!newUserVoiceStates[voiceState.user_id]) {
						newUserVoiceStates[voiceState.user_id] = {};
					}
					if (!newUserVoiceStates[voiceState.user_id][guild.id]) {
						newUserVoiceStates[voiceState.user_id][guild.id] = {};
					}
					newUserVoiceStates[voiceState.user_id][guild.id][connectionId] = vsWithGuild;
					newConnectionVoiceStates[connectionId] = vsWithGuild;
				}
			}
			this.setVoiceStates(newVoiceStates);
			this.setUserVoiceStates(newUserVoiceStates);
			this.setConnectionVoiceStates(newConnectionVoiceStates);
		});
		logger.info('Initialized voice states from connection open', {
			guildCount: guilds.length,
			totalVoiceStates: Object.keys(this.getConnectionVoiceStates()).length,
		});
	}
	handleGuildCreate(guild: GuildReadyData): void {
		runInAction(() => {
			const voiceStates = guild.voice_states ?? [];
			const newVoiceStates = {...this.getVoiceStates(), [guild.id]: {}};
			const newUserVoiceStates = {...this.getUserVoiceStates()};
			const newConnectionVoiceStates = {...this.getConnectionVoiceStates()};
			const guildVoiceStates: Record<string, Record<string, VoiceState>> = {};
			for (const [connectionId, voiceState] of Object.entries(newConnectionVoiceStates)) {
				if (voiceState.guild_id === guild.id) {
					delete newConnectionVoiceStates[connectionId];
				}
			}
			for (const [userId, userStates] of Object.entries(newUserVoiceStates)) {
				if (!userStates[guild.id]) {
					continue;
				}
				const updatedUserStates = {...userStates};
				delete updatedUserStates[guild.id];
				if (Object.keys(updatedUserStates).length === 0) {
					delete newUserVoiceStates[userId];
					continue;
				}
				newUserVoiceStates[userId] = updatedUserStates;
			}
			for (const voiceState of voiceStates) {
				if (!voiceState.channel_id) {
					continue;
				}
				const connectionId = voiceState.connection_id;
				if (!connectionId) {
					logger.warn('Skipping voice state without connection_id', {
						guildId: guild.id,
						userId: voiceState.user_id,
					});
					continue;
				}
				if (!guildVoiceStates[voiceState.channel_id]) {
					guildVoiceStates[voiceState.channel_id] = {};
				}
				const vsWithGuild = {...voiceState, guild_id: guild.id};
				guildVoiceStates[voiceState.channel_id][connectionId] = vsWithGuild;
				const userStates = {...(newUserVoiceStates[voiceState.user_id] ?? {})};
				const guildUserStates = {...(userStates[guild.id] ?? {})};
				guildUserStates[connectionId] = vsWithGuild;
				userStates[guild.id] = guildUserStates;
				newUserVoiceStates[voiceState.user_id] = userStates;
				newConnectionVoiceStates[connectionId] = vsWithGuild;
			}
			newVoiceStates[guild.id] = guildVoiceStates;
			this.setVoiceStates(newVoiceStates);
			this.setUserVoiceStates(newUserVoiceStates);
			this.setConnectionVoiceStates(newConnectionVoiceStates);
		});
		logger.debug('Guild voice states loaded', {
			guildId: guild.id,
			voiceStateCount: guild.voice_states?.length ?? 0,
		});
	}
	handleGuildDelete(deletedGuildId: string): void {
		const has = Boolean(this.getVoiceStates()[deletedGuildId]);
		if (!has) {
			logger.debug('No voice states to delete for guild', {guildId: deletedGuildId});
			return;
		}
		runInAction(() => {
			const newVoiceStates = {...this.getVoiceStates()};
			const newUserVoiceStates = {...this.getUserVoiceStates()};
			const newConnectionVoiceStates = {...this.getConnectionVoiceStates()};
			delete newVoiceStates[deletedGuildId];
			for (const [userId, userStates] of Object.entries(this.getUserVoiceStates())) {
				if (userStates[deletedGuildId]) {
					const updated = {...userStates};
					delete updated[deletedGuildId];
					if (Object.keys(updated).length === 0) {
						delete newUserVoiceStates[userId];
					} else {
						newUserVoiceStates[userId] = updated;
					}
				}
			}
			for (const [connectionId, voiceState] of Object.entries(this.getConnectionVoiceStates())) {
				if (voiceState.guild_id === deletedGuildId) {
					delete newConnectionVoiceStates[connectionId];
				}
			}
			this.setVoiceStates(newVoiceStates);
			this.setUserVoiceStates(newUserVoiceStates);
			this.setConnectionVoiceStates(newConnectionVoiceStates);
		});
		logger.info('Removed all voice states for deleted guild', {guildId: deletedGuildId});
	}
}
